import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Index from './pages/Index';
import NotFound from './pages/NotFound';
import Agents from './pages/Agents';
import AgentDashboard from './pages/AgentDashboard';
import AboutUs from './pages/AboutUs';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FAQ from './pages/FAQ';
import MyDashboard from './pages/MyDashboard';
import AllStates from './pages/AllStates';
import PropertyTaxReport from './pages/PropertyTaxReport';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import useGetProfile from './hooks/useGetProfile';

// State pages
import Alabama from './pages/states/Alabama';
import Alaska from './pages/states/Alaska';
import Arizona from './pages/states/Arizona';
import Arkansas from './pages/states/Arkansas';
import California from './pages/states/California';
import Colorado from './pages/states/Colorado';
import Connecticut from './pages/states/Connecticut';
import Delaware from './pages/states/Delaware';
import DistrictOfColumbia from './pages/states/DistrictOfColumbia';
import Florida from './pages/states/Florida';
import Georgia from './pages/states/Georgia';
import Hawaii from './pages/states/Hawaii';
import Idaho from './pages/states/Idaho';
import Illinois from './pages/states/Illinois';
import Indiana from './pages/states/Indiana';
import Iowa from './pages/states/Iowa';
import Kansas from './pages/states/Kansas';
import Kentucky from './pages/states/Kentucky';
import Louisiana from './pages/states/Louisiana';
import Maine from './pages/states/Maine';
import Maryland from './pages/states/Maryland';
import Massachusetts from './pages/states/Massachusetts';
import Michigan from './pages/states/Michigan';
import Minnesota from './pages/states/Minnesota';
import Mississippi from './pages/states/Mississippi';
import Missouri from './pages/states/Missouri';
import Montana from './pages/states/Montana';
import Nebraska from './pages/states/Nebraska';
import Nevada from './pages/states/Nevada';
import NewHampshire from './pages/states/NewHampshire';
import NewJersey from './pages/states/NewJersey';
import NewMexico from './pages/states/NewMexico';
import NewYork from './pages/states/NewYork';
import NorthCarolina from './pages/states/NorthCarolina';
import NorthDakota from './pages/states/NorthDakota';
import Ohio from './pages/states/Ohio';
import Oklahoma from './pages/states/Oklahoma';
import Oregon from './pages/states/Oregon';
import Pennsylvania from './pages/states/Pennsylvania';
import RhodeIsland from './pages/states/RhodeIsland';
import SouthCarolina from './pages/states/SouthCarolina';
import SouthDakota from './pages/states/SouthDakota';
import Tennessee from './pages/states/Tennessee';
import Texas from './pages/states/Texas';
import Utah from './pages/states/Utah';
import Vermont from './pages/states/Vermont';
import Virginia from './pages/states/Virginia';
import Washington from './pages/states/Washington';
import WestVirginia from './pages/states/WestVirginia';
import Wisconsin from './pages/states/Wisconsin';
import Wyoming from './pages/states/Wyoming';
import 'react-tooltip/dist/react-tooltip.css';

const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-4xl font-bold mb-4">{title}</h1>
      <p className="text-xl text-gray-600">This page is coming soon!</p>
      <a
        href="/"
        className="text-blue-500 hover:text-blue-700 underline block mt-4"
      >
        Return to Home
      </a>
    </div>
  </div>
);

const App = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  const { data: profile } = useGetProfile();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      getAccessTokenSilently()
        .then((res) => {
          const existingToken = window.localStorage.getItem('_token');
          window.localStorage.setItem('_token', res);

          // Only reload if we didn't have a token before and now we do
          if (!existingToken && res) {
            window.location.reload();
          }
        })
        .catch(console.log);
    } else if (!isLoading && !isAuthenticated) {
      window.localStorage.removeItem('_token');
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/faq" element={<FAQ />} />
          <Route
            path="/dashboard"
            element={
              profile?.role === 'AGENT' ? <AgentDashboard /> : <MyDashboard />
            }
          />
          <Route
            path="/get-started"
            element={<PlaceholderPage title="Get Started" />}
          />
          <Route path="/guides" element={<AllStates />} />
          <Route path="/all-states" element={<AllStates />} />
          <Route path="/deadlines" element={<AllStates />} />
          <Route path="/property-tax-report" element={<PropertyTaxReport />} />
          <Route path="/alabama" element={<Alabama />} />
          <Route path="/alaska" element={<Alaska />} />
          <Route path="/arizona" element={<Arizona />} />
          <Route path="/arkansas" element={<Arkansas />} />
          <Route path="/california" element={<California />} />
          <Route path="/colorado" element={<Colorado />} />
          <Route path="/connecticut" element={<Connecticut />} />
          <Route path="/delaware" element={<Delaware />} />
          <Route
            path="/district-of-columbia"
            element={<DistrictOfColumbia />}
          />
          <Route path="/florida" element={<Florida />} />
          <Route path="/georgia" element={<Georgia />} />
          <Route path="/hawaii" element={<Hawaii />} />
          <Route path="/idaho" element={<Idaho />} />
          <Route path="/illinois" element={<Illinois />} />
          <Route path="/indiana" element={<Indiana />} />
          <Route path="/iowa" element={<Iowa />} />
          <Route path="/kansas" element={<Kansas />} />
          <Route path="/kentucky" element={<Kentucky />} />
          <Route path="/louisiana" element={<Louisiana />} />
          <Route path="/maine" element={<Maine />} />
          <Route path="/maryland" element={<Maryland />} />
          <Route path="/massachusetts" element={<Massachusetts />} />
          <Route path="/michigan" element={<Michigan />} />
          <Route path="/minnesota" element={<Minnesota />} />
          <Route path="/mississippi" element={<Mississippi />} />
          <Route path="/missouri" element={<Missouri />} />
          <Route path="/montana" element={<Montana />} />
          <Route path="/nebraska" element={<Nebraska />} />
          <Route path="/nevada" element={<Nevada />} />
          <Route path="/new-hampshire" element={<NewHampshire />} />
          <Route path="/new-jersey" element={<NewJersey />} />
          <Route path="/new-mexico" element={<NewMexico />} />
          <Route path="/new-york" element={<NewYork />} />
          <Route path="/north-carolina" element={<NorthCarolina />} />
          <Route path="/north-dakota" element={<NorthDakota />} />
          <Route path="/ohio" element={<Ohio />} />
          <Route path="/oklahoma" element={<Oklahoma />} />
          <Route path="/oregon" element={<Oregon />} />
          <Route path="/pennsylvania" element={<Pennsylvania />} />
          <Route path="/rhode-island" element={<RhodeIsland />} />
          <Route path="/south-carolina" element={<SouthCarolina />} />
          <Route path="/south-dakota" element={<SouthDakota />} />
          <Route path="/tennessee" element={<Tennessee />} />
          <Route path="/texas" element={<Texas />} />
          <Route path="/utah" element={<Utah />} />
          <Route path="/vermont" element={<Vermont />} />
          <Route path="/virginia" element={<Virginia />} />
          <Route path="/washington" element={<Washington />} />
          <Route path="/west-virginia" element={<WestVirginia />} />
          <Route path="/wisconsin" element={<Wisconsin />} />
          <Route path="/wyoming" element={<Wyoming />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route
            path="/contact"
            element={<PlaceholderPage title="Contact Us" />}
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  );
};

export default App;
